/**
 * FeedbackSection Component
 *
 * Admin feedback UI for carrier prediction on time entries.
 * Combines PredictionToggle (approved/needs_improvement/rejected) with feedback textareas.
 *
 * - "Attorney Feedback" is always shown for general feedback
 * - "Feedback for Hourglass" appears when needs_improvement or rejected is selected
 * - "Rejection Reason" dropdown appears when rejected is selected (required)
 *
 * Used in AdminEntryDetailDialog for admin narrative review workflow.
 */

import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/components/ui/button";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { useDarkMode } from "@/shared/hooks/useDarkMode";
import {
  PredictionToggle,
  PredictionValue,
} from "@/shared/components/PredictionToggle";

// Predefined rejection reasons
const REJECTION_REASONS = [
  "Additional Details Required Fee", "Additional Information Required | Missing Receipts/Invoice - Expense", "Administrative", "Administrative Overhead", "Administrative Overhead Fee",
  "Administrative Tasks", "Approval - Motion", "Approval - Research", "Approval Required", "Approval/Prior Consultation Required - Exp",
  "Approval/Prior Consultation Required - Fees", "Approved Rate Deviation", "Associate", "Associate Level Task", "Authorization",
  "Billing Increments", "Block Billing", "Budget", "Budgets", "Calendaring/Scheduling",
  "Charges for Service (fee)", "Clerical", "Clerical Task", "Clerical Tasks", "Clerical/Administrative Tasks",
  "Clerical/Secretarial Task", "Communications", "Communications - Inadequately Described", "Docketing/Calendaring", "Document Preparation",
  "Document Review", "Documentation Required", "Documentation Required Expense", "Documentation of Expense", "Duplicate Entries Expense",
  "Duplicate Entries Fee", "Duplicate Entries Fees", "Duplicate Entry", "Duplicate Entry - Disb.", "Duplicate Entry - Fees",
  "Duplicate Entry -Fees", "Duplicate Entry Fee", "Duplicate-Conference", "Duplication of Effort", "Duplicative/Redundant",
  "Duplicative/Redundant/Fragmented", "Expense Consultation Required", "Expense Documentation", "Expense Documentation Required", "Expense over $1,000",
  "Expenses", "Express Mail", "Extended Work Day", "Extended Work Day | On this Claim", "External Expense",
  "File Review", "Form Documents", "Form or Brief Documents", "Inadequate Description", "Inadequate Description Expense",
  "Inadequate Description Fee", "Inadequate Descriptions", "Inadequate Descriptions Fees", "Inadequately Described 3rd Party Communications", "Inadequately Described Expense",
  "Inadequately Described Fees", "Inappropriately Billed Travel Time", "Incremental/Aggregate Billing", "Incremental/Patterned Billing", "Internal Expense",
  "Intra Office Conferences/Communications", "Invoice Processing", "Late Billing", "Late Entry Submission", "Legal Processing Fee",
  "Legal Research", "Math Error - Fees", "Math Error Expense", "Missing Approval/Template | Expert Vendor - Expense", "Motions/Appeals",
  "Multi-Docs", "Multiple Attendance", "Multiple Attendance - Fee", "Multiple Attendee Approval", "Multiple Documents",
  "Multiple Minimum/Related Tasks", "Multiple Staffing", "Necessity", "Nominal Task", "Nominal Task_Other",
  "Nominal Tasks", "Nominal Time", "Nominal Time Required", "Non Billable Overhead Disbursements", "Non-Billable Disbursements",
  "Non-Billable Expense", "Non-Billable Overhead Disbursement", "Non-Billable Overhead Disbursements", "Non-Billable Overhead Fee", "Non-Billable Overhead Fees",
  "Non-Billable Task", "Non-Billable Task/Order Records", "Non-Professional Task", "Non-Reimbursable Overhead | Administrative Activity", "Non-Reimbursable Overhead | Clerical Activity",
  "Non-Reimbursable Overhead | Invoice Processing Activity", "Non-Reimbursable Overhead | Non-Billable Activity", "Non-Reimbursable Overhead | Routine Enclosures", "Non-Reimbursable Overhead | Routine Form/Template", "Non-Reimbursable Overhead | Scheduling Activity",
  "Non-Reimbursable | Activity After the Close Date - Fee", "Non-Reimbursable | Duplicate/Previously Billed - Fee", "Non-Reimbursable | Duplication of Effort", "Non-Reimbursable | Redundant Task - Fee", "Non-Reimbursable | Unauthorized Vendor Utilization",
  "Non-billable Tasks", "Office Communications", "Office Conferences", "Office Conferences/Communications", "Office Conferencing",
  "Other", "Other - Disbursement", "Other - Fees", "Other Expenses", "Other Fees",
  "PA - Expert/Consultant/Local Counsel", "Paralegal", "Paralegal (attorneys billing)", "Paralegal Activities", "Paralegal Task",
  "Paralegal Work Performed by Attorney", "Partner to Associate rate", "Pre-approval required", "Prior Approval", "Purpose/Necessity",
  "Questionable Time", "Rate Deviation", "Reasonableness Of Time", "Reasonableness of Time", "Reasonablenss of time/supporting documentation",
  "Record Review/Summary/Chronology", "Record Summaries", "Redundant", "Research", "Review/revise work product of another",
  "Reworking/Editing/Review", "Routine File Review", "Sched_Other", "Scheduling", "Secretarial Tasks",
  "Secretarial/Clerical Fee", "Specific Activities", "Staffing", "Staffing-Multiple TK", "Stale Billing",
  "Stale Expenses", "Stale Fees", "Stale Time - Expenses", "Stale Time - Fees", "Stale billing",
  "Time Entry Assessment", "Timekeeper Unknown", "Timekeeper | Multiple Timekeeper Attendance", "Timekeeper | Paralegal Rate Applies: Activity Described Meets NALA Standards", "Timekeeper | Rate: Supporting Attorney",
  "Timekeeper | Unapproved Rate", "Travel - Local (expense)", "Travel - first 50 miles", "Travel Expense", "Travel Time",
  "Travel | Timekeeper Travel Rate - Fee", "Travel | Unapproved - Expense", "Unit of Time | Reasonableness of Time Based on Timekeeper Description", "Unknown/Unapproved Timekeeper", "Unknown/Unapproved Timekeepers",
  "Unknown/Unapproved timekeepers", "Unrelated Case", "Unrelated Entry Fee", "Untimely - Fee", "Vague Description",
  "Work Product Required", "Work Product Required-50%",
];

// ─────────────────────────────────────────────────────────────────────────────
// RejectionReasonPicker - Searchable dropdown with custom text entry
// ─────────────────────────────────────────────────────────────────────────────

interface RejectionReasonPickerProps {
  value: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
  error?: boolean;
}

function RejectionReasonPicker({
  value,
  onChange,
  disabled = false,
  error = false,
}: RejectionReasonPickerProps) {
  const { darkMode } = useDarkMode();
  const [open, setOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");

  // Filter predefined options based on search
  const filteredReasons = React.useMemo(() => {
    if (!searchTerm) return REJECTION_REASONS;
    const lowerSearch = searchTerm.toLowerCase();
    return REJECTION_REASONS.filter((reason) =>
      reason.toLowerCase().includes(lowerSearch)
    );
  }, [searchTerm]);

  // Check if current search term exactly matches a predefined option
  const hasExactMatch = React.useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase().trim();
    return REJECTION_REASONS.some(
      (reason) => reason.toLowerCase() === lowerSearch
    );
  }, [searchTerm]);

  // Show custom option if there's text and no exact match
  const showCustomOption = searchTerm.trim() && !hasExactMatch;

  // Dark mode styling
  const darkModeButton = darkMode
    ? "text-[#abb2bf] bg-[#21252b] border-[#3e4451] hover:bg-[#3e4451] hover:text-[#abb2bf]"
    : "hover:bg-gray-100";

  const darkModePopover = darkMode
    ? "text-[#abb2bf] bg-[#21252b] border-[#3e4451]"
    : "";

  const darkModeCommand = darkMode
    ? "text-[#abb2bf] bg-[#21252b] border-[#3e4451] [&>div:nth-child(2)]:border-t [&>div:nth-child(2)]:border-[#3e4451]"
    : "";

  const darkModeInput = darkMode
    ? "text-[#abb2bf] bg-[#21252b] border-[#3e4451]"
    : "";

  const darkModeItem = darkMode
    ? "hover:!bg-[#1E4D5A] hover:!text-[#abb2bf] text-[#abb2bf] bg-[#21252b] aria-selected:!bg-[#1E4D5A] aria-selected:!text-[#abb2bf]"
    : "";

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    setOpen(false);
    setSearchTerm("");
  };

  return (
    <Popover
      open={disabled ? false : open}
      onOpenChange={disabled ? undefined : setOpen}
      modal={true}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full text-left justify-between font-normal overflow-hidden",
            darkModeButton,
            disabled && "opacity-50 cursor-not-allowed",
            error && darkMode && "border-red-700 bg-red-900/40",
            error && !darkMode && "border-red-500 bg-red-50"
          )}
        >
          <span className={cn(!value && "text-muted-foreground")}>
            {value || "Select rejection reason..."}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn("w-[400px] p-0 text-left", darkModePopover)}
      >
        <Command shouldFilter={false} className={cn(darkModeCommand)}>
          <CommandInput
            placeholder="Search or type custom reason..."
            value={searchTerm}
            onValueChange={setSearchTerm}
            className={cn(darkModeInput)}
          />
          <CommandList>
            <CommandEmpty>No matching reasons found.</CommandEmpty>
            <CommandGroup className={cn(darkModePopover)}>
              {/* Custom text option - shown when typing and no exact match */}
              {showCustomOption && (
                <CommandItem
                  value={`custom-${searchTerm}`}
                  onSelect={() => handleSelect(searchTerm.trim())}
                  className={cn(darkModeItem, "italic")}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === searchTerm.trim() ? "opacity-100" : "opacity-0"
                    )}
                  />
                  Use: "{searchTerm.trim()}"
                </CommandItem>
              )}

              {/* Predefined options */}
              {filteredReasons.map((reason) => (
                <CommandItem
                  key={reason}
                  value={reason}
                  onSelect={() => handleSelect(reason)}
                  className={cn(darkModeItem)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === reason ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {reason}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FeedbackSection - Main component
// ─────────────────────────────────────────────────────────────────────────────

interface FeedbackSectionProps {
  prediction: PredictionValue;
  attorneyFeedback: string;
  onPredictionChange: (value: PredictionValue) => void;
  onAttorneyFeedbackChange: (value: string) => void;
  hourglassFeedback?: string;
  onHourglassFeedbackChange?: (value: string) => void;
  rejectionReason?: string | null;
  onRejectionReasonChange?: (value: string | null) => void;
  disabled?: boolean;
}

export default function FeedbackSection({
  prediction,
  attorneyFeedback,
  onPredictionChange,
  onAttorneyFeedbackChange,
  hourglassFeedback = "",
  onHourglassFeedbackChange,
  rejectionReason,
  onRejectionReasonChange,
  disabled,
}: FeedbackSectionProps) {
  const showHourglassFeedback = prediction === "needs_improvement" || prediction === "rejected";
  const showRejectionReason = prediction === "rejected" && onRejectionReasonChange;

  return (
    <div className="grid gap-4 pt-4 border-t border-border">
      <div className="grid gap-2">
        <Label className="text-sm font-medium">Carrier Prediction</Label>
        <PredictionToggle
          value={prediction}
          onChange={onPredictionChange}
          disabled={disabled}
        />
      </div>

      {/* Rejection Reason - required when rejected is selected */}
      {showRejectionReason && (
        <div className="grid gap-2">
          <Label className="text-sm font-medium">
            Rejection Reason <span className="text-red-500">*</span>
          </Label>
          <RejectionReasonPicker
            value={rejectionReason ?? null}
            onChange={onRejectionReasonChange}
            disabled={disabled}
            error={!rejectionReason}
          />
        </div>
      )}

      <div className="grid gap-2">
        <Label htmlFor="attorney-feedback" className="text-sm font-medium">
          Attorney Feedback (optional)
        </Label>
        <Textarea
          id="attorney-feedback"
          placeholder="Feedback for the attorney about this entry"
          value={attorneyFeedback}
          onChange={(e) => onAttorneyFeedbackChange(e.target.value)}
          disabled={disabled}
          rows={3}
          className="resize-none"
        />
      </div>

      {showHourglassFeedback && onHourglassFeedbackChange && (
        <div className="grid gap-2">
          <Label htmlFor="hourglass-feedback" className="text-sm font-medium">
            Feedback for Hourglass (optional)
          </Label>
          <Textarea
            id="hourglass-feedback"
            placeholder="How can Hourglass improve the AI-generated narrative?"
            value={hourglassFeedback}
            onChange={(e) => onHourglassFeedbackChange(e.target.value)}
            disabled={disabled}
            rows={3}
            className="resize-none"
          />
        </div>
      )}
    </div>
  );
}
